import { navMessagesId } from "@/assets/googleTrackIds";
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Badge, Button, Divider, Flex, Grid, Layout, Menu, Space, prefixCls, theme } from "@payping/web-components";
import { useAsync } from "react-use";
import { DashboardRoutes } from "@/router/constant";
import { PhRBell } from "@payping/icons";
import { getUserAvailableBanks, getUserMessages, getUserProfile, getUserWage } from "@payping/redux/actions";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import classNames from "classnames";
import { UserProfile } from "./subSection/UserProfile";
import { useItems } from "./subSection/useItems";
import { BusinessSection } from "./subSection/BusinessSection";
import { useSelectedTab } from "./useSelectedTab";
import { Update } from "./subSection/Update";
import OverviewTour from "./Tour";
import "./MainNav.scss";
import "./MobileMainNav.scss";
import { buildUrl } from "@payping/utilities-shared/url";
import { accountApis } from "@payping/api";
import { URLS } from "@payping/utilities-shared/consts";

const AppMainNav: React.FC = () => {
  const { home } = DashboardRoutes;
  const location = useLocation();
  const dispatch = useAppDispatch();
  const selectedTab = useSelectedTab();
  const personalInfo = useAppSelector((state) => state.user.personalInfo);
  const messageData = useAppSelector((state) => state.user.message);
  const [visibleAfterLogo, setVisibleAfterLogo] = useState<boolean>();
  const { token } = theme.useToken();
  const { lg } = Grid.useBreakpoint();

  useAsync(() => dispatch(getUserMessages({ limit: 5, offset: 0 })), [dispatch]);
  const { value: shahkarValid } = useAsync(async () => await accountApis.shahkarValidationStatusGet(), [dispatch]);
  useAsync(() => dispatch(getUserProfile()), [dispatch]);
  useAsync(() => dispatch(getUserWage()), [dispatch]);
  useAsync(() => dispatch(getUserAvailableBanks()), [dispatch]);

  useEffect(() => {
    if (shahkarValid && !shahkarValid.isValid) {
      window.location.href = buildUrl(URLS.AUTH_URL, "auth/number").toString();
    }
  }, [shahkarValid]);

  useEffect(() => {
    if (personalInfo) {
      const { email, id, username, businessName, firstName, lastName } = personalInfo;
      Sentry.configureScope((scope) => {
        scope.setUser({ email, id: id?.toString(), username });
        scope.setExtra("firstName", firstName);
        scope.setExtra("lastName", lastName);
        scope.setExtra("businessName", businessName);
      });
    }
  }, [personalInfo]);

  const items = useItems();

  const NotificationElement = () => (
    <Link to={home.messages.url} data-google-track={navMessagesId}>
      <Button
        type={"text"}
        icon={
          <Badge dot={Boolean(messageData?.list[0]?.isSeen === false ? 1 : 0)}>
            <PhRBell width={20} />
          </Badge>
        }
      />
    </Link>
  );

  const desktopElement = (
    <Layout className={`${prefixCls}-web-mainNav-wrapper`} style={{ backgroundColor: token.colorBgContainer }}>
      <div className={`${prefixCls}-web-mainNav`}>
        <Space size={"middle"} className={`${prefixCls}-web-mainNav-section`}>
          <Space className={`${prefixCls}-web-mainNav-logo`}>
            <BusinessSection />
          </Space>
          <Divider type={"vertical"} />
        </Space>
        <Menu
          selectedKeys={[selectedTab]}
          mode={"horizontal"}
          data-testid={"tabs"}
          style={{ backgroundColor: token.colorBgContainer }}
          items={items.map(({ key, title, ...otherItemProps }) => ({
            key,
            label: (
              <Link key={key} to={key}>
                {title}
              </Link>
            ),
            ...otherItemProps,
          }))}
        />
        <Space size={"middle"} className={`${prefixCls}-web-mainNav-section`}>
          <Update />
          <NotificationElement />
          <UserProfile />
        </Space>
      </div>
    </Layout>
  );

  // جهت نمایش نوبار بالا صفه موبایل فقط در روت منو های اصلی مانند:
  // /settlement /connections /myShop etc..
  const urlsMobileWithNavbar: string[] = [
    DashboardRoutes.digitalAccount.root.url,
    DashboardRoutes.settlement.root.url,
    DashboardRoutes.myShop.root.url,
    DashboardRoutes.mySale.root.url,
    DashboardRoutes.connections.root.url,
    DashboardRoutes.home.overview.url,
    // Add more URLs as needed
  ];
  const showMobileUpperNavigation = urlsMobileWithNavbar.some((url) => url === location.pathname);

  const mobileElement = (
    <>
      {/* < موبایل نویگیشن بالا > */}
      {showMobileUpperNavigation ? (
        <Flex
          gap={"middle"}
          justify={"space-between"}
          align={"center"}
          className={classNames(`${prefixCls}-web-mainNav-drawer-wrapper`, { "top-layer": visibleAfterLogo })}
          style={{
            backgroundColor: token.colorBgContainer,
            boxShadow: "0px 0px 8px 0px #0000001A",
            marginBottom: "8px",
          }}
          id={"mainNav-drawer"}
        >
          <div onClick={() => setVisibleAfterLogo((prev) => !prev)}>
            <BusinessSection />
          </div>
          <Flex gap={"small"} justify={"flex-end"}>
            <NotificationElement />

            <UserProfile />
          </Flex>
        </Flex>
      ) : null}
      {/* < موبایل نویگیشن پایین > */}
      <Flex className={classNames(`${prefixCls}-web-mainNav-phone`)} justify={"space-evenly"}>
        {items?.map(({ title, Icon, key, ...otherItemProps }) => (
          <Link key={key} className={selectedTab === key ? "cta" : "color--black"} to={key}>
            <Flex vertical align={"center"} justify={"center"} gap={"small"} {...otherItemProps}>
              <Icon width={"1rem"} />
              {title}
            </Flex>
          </Link>
        ))}
      </Flex>
    </>
  );

  return (
    <>
      {lg ? desktopElement : mobileElement}
      <OverviewTour />
    </>
  );
};

export default AppMainNav;
